import * as React from 'react';
import { Id } from '../../utils/ids.js';
import { addClass, Bem } from '../../utils/css.js';
import { anyUndefined } from '../../utils/common.js';
import './Tabs.scss.js';
import { Key } from '../../constants.js';

const TabGap = React.forwardRef(function TabGap2(props, ref) {
  const { orientation = "horizontal" } = props;
  if (orientation === "vertical") {
    return /* @__PURE__ */ React.createElement("div", {
      className: "vai-side-tab-gap",
      ref
    });
  }
  return /* @__PURE__ */ React.createElement("div", {
    className: "vai-tab-gap",
    ref
  });
});
const previousIndex = (currentIndex, items) => {
  if (currentIndex === 0) {
    return items - 1;
  }
  return currentIndex - 1;
};
const nextIndex = (currentIndex, items) => {
  if (currentIndex === items - 1) {
    return 0;
  }
  return currentIndex + 1;
};
const interceptedKeys = [Key.Home, Key.End, Key.ArrowLeft, Key.ArrowRight, Key.ArrowUp, Key.ArrowDown, Key.Space];
const interceptedHorizontalKeys = [Key.ArrowLeft, Key.ArrowRight];
const interceptedVerticalKeys = [Key.ArrowUp, Key.ArrowDown];
const TabsContainer = React.forwardRef(function TabsContainer2(props, ref) {
  const { children, className, dataTa, htmlId, selectedTabKey, setSelectedTabKey, htmlIdName, containerType, orientation, onSelectedTabChanged } = props;
  const id = Id.create(htmlId, [htmlIdName]);
  const bem = Bem.create(containerType);
  const [focusTabKey, setFocusTabKey] = React.useState(selectedTabKey);
  let selectedTabContents;
  const tabs = React.Children.map(children, (c, index) => {
    if (!React.isValidElement(c)) {
      return null;
    }
    const child = c;
    const { tabKey, htmlId: tabHtmlId, title, children: tabChildren } = child.props;
    if (tabKey === selectedTabKey) {
      selectedTabContents = React.Children.map(tabChildren, (cc) => {
        if (!React.isValidElement(cc)) {
          return null;
        }
        const contentChild = cc;
        return React.cloneElement(contentChild, {});
      });
    }
    return React.cloneElement(child, {
      htmlId: tabHtmlId || id.copyWith([index.toString()]).toHtmlId(),
      tabKey,
      title,
      isSelected: tabKey === selectedTabKey,
      isFocus: tabKey === focusTabKey,
      onChangeSelectedTab: onSelectedTabChanged,
      orientation
    });
  });
  const handleKeyDown = (e) => {
    const allInterceptedKeys = interceptedKeys.concat(orientation === "horizontal" ? interceptedHorizontalKeys : interceptedVerticalKeys);
    if (anyUndefined(selectedTabKey, focusTabKey, setSelectedTabKey, setFocusTabKey) || !allInterceptedKeys.includes(e.key)) {
      return;
    }
    const childrenArray = React.Children.toArray(children);
    const currentIndex = childrenArray.findIndex((child) => child.props.tabKey === focusTabKey);
    const [previousTabKey, nextTabKey, firstTabKey, lastTabKey, currentTabKey] = [
      previousIndex(currentIndex, childrenArray.length),
      nextIndex(currentIndex, childrenArray.length),
      0,
      childrenArray.length - 1,
      currentIndex
    ].map((index) => childrenArray[index].props.tabKey);
    if (childrenArray.length === 0 || currentIndex === -1) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    switch (e.key) {
      case Key.ArrowLeft:
        setFocusTabKey(previousTabKey);
        break;
      case Key.ArrowUp:
        if (orientation === "vertical") {
          setFocusTabKey(previousTabKey);
        }
        break;
      case Key.ArrowRight:
        setFocusTabKey(nextTabKey);
        break;
      case Key.ArrowDown:
        if (orientation === "vertical") {
          setFocusTabKey(nextTabKey);
        }
        break;
      case Key.Home:
        setFocusTabKey(firstTabKey);
        break;
      case Key.End:
        setFocusTabKey(lastTabKey);
        break;
      case Key.Space:
        setSelectedTabKey(currentTabKey);
        break;
    }
  };
  return /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    "data-ta": dataTa,
    className: addClass(bem.block(), className),
    ref
  }, /* @__PURE__ */ React.createElement("div", {
    className: bem.element("tabs-row")(),
    onKeyDown: handleKeyDown
  }, /* @__PURE__ */ React.createElement(TabGap, {
    orientation
  }), tabs, /* @__PURE__ */ React.createElement("div", {
    className: bem.element("tab-filler")()
  })), /* @__PURE__ */ React.createElement("div", {
    className: bem.element("tab-content")()
  }, selectedTabContents));
});

export { TabGap, TabsContainer };
